import React from 'react'

import { useSelector } from 'react-redux'

import { usePermission } from '@/core/hooks/usePermission'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import { AccountTab } from '@/features/account/types'
import { cn } from '@/shared/utils/helpers'

// Countries that have PRO organizations defined
const SUPPORTED_COUNTRIES = [
	'AR',
	'MX',
	'ES',
	'CO',
	'CL',
	'PA',
	'PE',
	'EC',
	'BO',
	'CR',
	'DO',
	'SV',
	'GT',
	'HN',
	'NI',
	'UY',
	'VE',
]

interface AccountNavigationProps {
	activeTab: AccountTab
	onTabChange: (tab: AccountTab) => void
}

const AccountNavigation: React.FC<AccountNavigationProps> = ({
	activeTab,
	onTabChange,
}) => {
	const { t } = useTranslation('common')
	const { isBillingEnabled, features } = usePermission()
	const { user } = useSelector((state: RootState) => state.auth)

	// Check if music legal compliance tab should be visible
	const userCountry = user?.account?.country
	const isSoundreef = user?.account?.royalty?.name === 'Soundreef'
	const hasOrganizations = userCountry
		? SUPPORTED_COUNTRIES.includes(userCountry)
		: false
	const isComplianceEnabled = features.account.musicLegalCompliance
	const showMusicLegalCompliance =
		isComplianceEnabled && (isSoundreef || hasOrganizations)
	const showSubscriptionSettings = isBillingEnabled

	const tabs = [
		{
			id: 'user-profile' as AccountTab,
			label: t('account.navigation.user_profile'),
			visible: true,
		},
		{
			id: 'brand-profile' as AccountTab,
			label: t('account.navigation.brand_profile'),
			visible: true,
		},
		{
			id: 'subscription' as AccountTab,
			label: t('account.navigation.subscription'),
			visible: showSubscriptionSettings,
		},
		{
			id: 'billing' as AccountTab,
			label: t('account.navigation.billing'),
			visible: showSubscriptionSettings,
		},
		{
			id: 'users' as AccountTab,
			label: t('account.navigation.users'),
			visible: true,
		},
		{
			id: 'music-legal-compliance' as AccountTab,
			label: t('account.navigation.music_legal_compliance'),
			visible: showMusicLegalCompliance,
		},
	].filter((tab) => tab.visible)

	return (
		<nav className="flex lg:flex-col">
			{tabs.map((tab) => (
				<button
					key={tab.id}
					onClick={() => onTabChange(tab.id)}
					className={cn(
						'w-full lg:text-left text-center p-1 lg:px-3 lg:py-2 rounded-md text-sm font-medium transition-colors',
						activeTab === tab.id
							? 'bg-muted text-foreground'
							: 'text-muted-foreground hover:text-foreground'
					)}
				>
					{tab.label}
				</button>
			))}
		</nav>
	)
}

export default AccountNavigation
